import { useCallback, useState } from 'react'

/**
 * Comandos de câmera do ambiente 3D.
 *
 * O contrato vive fora do `Canvas` porque a trilha de ferramentas aciona os
 * comandos e a barra de estado precisa saber qual vista está ativa.
 *
 * Nada aqui decide conteúdo didático: são poses de observação. O que se vê não
 * muda, só de onde se olha.
 */

export type TipoComandoCamera =
  | 'aproximar'
  | 'afastar'
  | 'restaurar'
  | 'liberarCamera'
  | 'enquadrarSelecao'
  /** Todos os objetos selecionáveis no quadro (Table top: a cena montada inteira). */
  | 'enquadrarCena'
  | 'vistaFrontal'
  | 'vistaLateral'
  | 'vistaSuperior'
  | 'vistaIsometrica'
  /** Retoma o enquadramento anterior ao último comando de vista/enquadramento. */
  | 'voltarEnquadramento'

export interface ComandoCamera {
  /** Cresce a cada acionamento: é o que faz repetir o mesmo comando ter efeito. */
  id: number
  tipo: TipoComandoCamera
  /** Objeto a enquadrar, quando o comando é `enquadrarSelecao`. */
  idAlvo?: string | null
}

export type Vetor = [number, number, number]

/** Prefixo dos objetos que podem ser enquadrados. */
export const PREFIXO_SELECIONAVEL = 'selecionavel:'

/**
 * Caixa que a câmera precisa enquadrar, em metros: o caminhão-tanque numa
 * oficina, a área inteira de isolamento noutra. Centrada na origem, apoiada
 * no chão. Substitui o perfil de veículo que o CSV Lab usava aqui.
 */
export interface VolumeEnquadramento {
  comprimento: number
  largura: number
  altura: number
  /** Centro do volume no chão (x, z), quando não é a origem — a pluma vai longe a favor do vento. */
  centro?: readonly [number, number]
  alturaAlvoM?: number
  /**
   * Raio de observação (m) do enquadramento padrão e das vistas nomeadas,
   * quando o foco relevante é menor que o volume inteiro — uma mesa com
   * zonas de risco bem maiores que os objetos, ou uma bola de fogo menor
   * que a Zona Morna. `comprimento`/`largura`/`altura` continuam mandando
   * no teto de zoom, nas sombras e no plano de corte distante.
   */
  raioInicialM?: number
}

const deslocar = (v: Vetor, volume: VolumeEnquadramento): Vetor =>
  volume.centro ? [v[0] + volume.centro[0], v[1], v[2] + volume.centro[1]] : v

export const DISTANCIA_MINIMA = 2.4
/** Teto padrão; a oficina pode configurar um alcance maior para zonas e plumas. */
export const DISTANCIA_MAXIMA = 2500

/** Direção da pose padrão: três quartos dianteiro, pelo lado esquerdo, de cima. */
const DIRECAO_ISOMETRICA: Vetor = [0.62, 0.43, 0.66]

/** Alvo das vistas: sobre a origem, a pouco menos da meia altura do volume. */
export const alvoCamera = (volume: VolumeEnquadramento): Vetor => deslocar([0, volume.alturaAlvoM ?? volume.altura * 0.4, 0], volume)

/**
 * Raio de observação das vistas nomeadas, derivado do próprio volume — um
 * caminhão e uma zona de 800 m não se enquadram da mesma distância. O fator vem
 * de enquadrar o maior lado com folga nas bordas.
 */
export function raioDeObservacao(volume: VolumeEnquadramento, aspecto = 1, distanciaMaxima = DISTANCIA_MAXIMA): number {
  // Tela em pé (celular): o campo horizontal é o mais estreito e manda na distância.
  const fatorAspecto = Math.min(2.2, Math.max(1, 1 / aspecto))
  if (volume.raioInicialM != null) {
    return Math.min(distanciaMaxima, Math.max(DISTANCIA_MINIMA, volume.raioInicialM * fatorAspecto))
  }
  const maiorLado = Math.max(volume.comprimento, volume.largura, volume.altura)
  return Math.min(distanciaMaxima, Math.max(DISTANCIA_MINIMA, maiorLado * 1.1 * fatorAspecto))
}

/** Pose padrão para um volume: a isométrica, à distância de observação. */
export function posicaoPadrao(volume: VolumeEnquadramento, aspecto = 1, distanciaMaxima = DISTANCIA_MAXIMA): Vetor {
  const raio = raioDeObservacao(volume, aspecto, distanciaMaxima)
  return deslocar([DIRECAO_ISOMETRICA[0] * raio, DIRECAO_ISOMETRICA[1] * raio, DIRECAO_ISOMETRICA[2] * raio], volume)
}

/**
 * Posição de câmera de cada vista nomeada.
 *
 * As vistas não são ortográficas: a projeção continua em perspectiva, e elas
 * apenas alinham a câmera a um eixo. Uma vista superior perfeitamente a pino
 * deixaria os controles de órbita sem referência de rotação, então ela guarda um
 * desvio pequeno em Z.
 */
export function posicaoDaVista(tipo: TipoComandoCamera, volume: VolumeEnquadramento, aspecto = 1, distanciaMaxima = DISTANCIA_MAXIMA): Vetor | null {
  const raio = raioDeObservacao(volume, aspecto, distanciaMaxima)
  const altura = alvoCamera(volume)[1]

  switch (tipo) {
    case 'vistaFrontal':
      return deslocar([0, altura, raio], volume)
    case 'vistaLateral':
      return deslocar([raio, altura, 0], volume)
    case 'vistaSuperior':
      return deslocar([0, raio, raio * 0.08], volume)
    case 'vistaIsometrica':
    case 'restaurar':
      return posicaoPadrao(volume, aspecto, distanciaMaxima)
    default:
      return null
  }
}

/** Comandos que deixam a câmera parada numa pose reconhecível. */
export const VISTAS_NOMEADAS = [
  'vistaFrontal',
  'vistaLateral',
  'vistaSuperior',
  'vistaIsometrica',
] as const satisfies readonly TipoComandoCamera[]

export type VistaNomeada = (typeof VISTAS_NOMEADAS)[number]

export function ehVistaNomeada(tipo: TipoComandoCamera): tipo is VistaNomeada {
  return (VISTAS_NOMEADAS as readonly TipoComandoCamera[]).includes(tipo)
}

export interface ControleCamera {
  comando: ComandoCamera
  executar: (tipo: TipoComandoCamera, idAlvo?: string | null) => void
  /** Última vista nomeada acionada, para marcar o botão correspondente. */
  vistaAtiva: VistaNomeada | null
}

export function useControleCamera(): ControleCamera {
  const [comando, setComando] = useState<ComandoCamera>({ id: 0, tipo: 'restaurar' })
  const [vistaAtiva, setVistaAtiva] = useState<VistaNomeada | null>(null)

  const executar = useCallback((tipo: TipoComandoCamera, idAlvo?: string | null) => {
    setComando((atual) => ({ id: atual.id + 1, tipo, idAlvo }))
    // Zoom e enquadramento não são vistas: partem de onde a câmera estiver e
    // param em lugar qualquer. Manter o botão de vista aceso depois deles
    // mentiria sobre onde a câmera ficou.
    setVistaAtiva(ehVistaNomeada(tipo) ? tipo : null)
  }, [])

  return { comando, executar, vistaAtiva }
}
